import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext.tsx';
import { dealFormField } from '@openx/types/deal.ts';
import type { BuyerOptionsMap } from '@openx/utils/apiHooks/useBuyersFetch.ts';
import { useCachedFlag } from '@openx/utils/hooks/useCachedFlag.ts';
import { isDateGreaterThan } from '@openx/utils/lib/compareDates.ts';
import { produceSortedOptionsKeys } from '@openx/utils/lib/sortedOptions.ts';
import { ENABLE_TTD_DATE, TTD_DEMAND_PARTNER_ID } from '@openx/utils/services/config.ts';
import isEqual from 'lodash/isEqual';
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DemandPartnerType } from '../../../utils/participantFunctions.ts';
import { CommercialAgreementsInfo } from '../../DealRevenueCalculator/DealRevenueModifiersContext/components/CommercialAgreementsInfo.tsx';
import { PostAuctionDiscountInfo } from '../../DealRevenueCalculator/DealRevenueModifiersContext/components/PostAuctionDiscountInfo.tsx';
import { useHasDealRevenueCalculatorAccess } from '../../DealRevenueCalculator/hooks/useHasDealRevenueCalculatorAccess.ts';
import { DealParticipantsField } from '../fields.ts';
import { useCheckBuyerSeatRestriction } from '../hooks/useCheckBuyerSeatRestriction.ts';
import { useSetFormFields } from '../hooks/useSetFormFields.ts';
import {
  getByersWithoutMainBuyer,
  getDeletedSeats,
  getMainBuyerOptions,
  prepareBuyerIdsForBuyerChange,
} from '../utils/ParticipantBuyers-utils.ts';
import { ParticipantBuyersMultiple } from './ParticipantBuyersMultiple.tsx';
import { ParticipantBuyersSingle } from './ParticipantBuyersSingle.tsx';
import { ParticipantBuyersTTD } from './ParticipantBuyersTTD.tsx';

type ParticipantBuyersProps = {
  buyers: BuyerOptionsMap;
  demandPartnerType?: DemandPartnerType;
  fieldPrefix: string;
  index: number;
  isBuyersLoading: boolean;
  isEdit: boolean;
  isTTDPartnerAndGuaranteedDealType: boolean;
};

export const ParticipantBuyers = ({
  buyers,
  demandPartnerType,
  fieldPrefix,
  index,
  isBuyersLoading,
  isEdit,
  isTTDPartnerAndGuaranteedDealType,
}: ParticipantBuyersProps) => {
  const { t } = useTranslation();
  const { setValue, control } = useFormContext();
  const hasDealRevenueCalculatorAccess = useHasDealRevenueCalculatorAccess();
  const multipleBuyersValuesRef = useRef<string[]>([]);
  const multipleBuyersIdsRef = useRef<string[]>([]);
  const singleBuyerIdsRef = useRef<string[]>([]);
  const { useCurrentInstanceState } = useTargetingContext();
  const { isBefInstance, currentInstanceUid } = useCurrentInstanceState();
  const [isFirstTimeTDD, setIsFirstTimeTDD] = useCachedFlag(`${currentInstanceUid}-isFirstTimeTDD`, false);
  const [isVisibleFirstTimeAlert, setIsVisibleFirstTimeAlert] = useState(false);

  const observableIsDV360IP = useWatch({ control, name: dealFormField.ENABLE_DV360_INVENTORY_PACKAGE });
  const observableBuyerIds = useWatch({ control, name: `${fieldPrefix}${DealParticipantsField.BUYER_IDS}` });
  const observableMainBuyer = useWatch({ control, name: `${fieldPrefix}${DealParticipantsField.MAIN_BUYER}` });
  const observableBuyerId = useWatch({ control, name: `${fieldPrefix}${DealParticipantsField.BUYER_ID}` });
  const observableDealType = useWatch({ control, name: dealFormField.PMP_DEAL_TYPE });
  const observableDemandPartner = useWatch({ control, name: `${fieldPrefix}${DealParticipantsField.DSP}` });
  const observableCreatedDate = useWatch({ control, name: 'created_date' });

  const isCreatedDateAfterEnabledTTD = useMemo(() => {
    return isDateGreaterThan(observableCreatedDate, ENABLE_TTD_DATE);
  }, [observableCreatedDate]);

  const isTTDPartnerType = observableDemandPartner === TTD_DEMAND_PARTNER_ID;

  const isEnabledDV360IP = !!observableIsDV360IP;
  const buyersOptions = useMemo(() => produceSortedOptionsKeys(buyers), [buyers]);
  const mainBuyerOptions = useMemo(
    () => getMainBuyerOptions(observableBuyerIds, buyersOptions),
    [buyersOptions, observableBuyerIds]
  );
  const filteredBuyersOption = useMemo(
    () => getByersWithoutMainBuyer(observableMainBuyer, buyersOptions),
    [buyersOptions, observableMainBuyer]
  );

  const buyersOptionsMap = useMemo(() => buyers || {}, [buyers]);

  const shouldDisplayTTD =
    isBefInstance && observableDealType === '3' && isTTDPartnerType && isCreatedDateAfterEnabledTTD;

  const showParticipantBuyersTTD = demandPartnerType === DemandPartnerType.TTD || shouldDisplayTTD;
  const showParticipantBuyersSingle = demandPartnerType === DemandPartnerType.DV360 && !isEnabledDV360IP;
  const showParticipantBuyersMultiple =
    ((demandPartnerType === DemandPartnerType.DV360 && isEnabledDV360IP) ||
      demandPartnerType === DemandPartnerType.XANDR ||
      demandPartnerType === DemandPartnerType.DEFAULT) &&
    !shouldDisplayTTD;
  useSetFormFields(
    isEdit,
    showParticipantBuyersMultiple,
    {
      multipleBuyersIdsRef,
      multipleBuyersValuesRef,
      singleBuyerIdsRef,
    },
    fieldPrefix
  );

  useCheckBuyerSeatRestriction({
    fieldPrefix,
    buyers,
    showParticipantBuyersTTD,
    showParticipantBuyersSingle,
    showParticipantBuyersMultiple,
  });

  useEffect(() => {
    if (observableBuyerIds.some(buyer => buyer === null)) return;
    const deletedSeats: (string | null)[] = getDeletedSeats(observableBuyerIds, buyers, isBuyersLoading);

    if (!isEqual(deletedSeats, observableBuyerIds)) {
      setValue(`${fieldPrefix}${DealParticipantsField.BUYER_IDS}`, deletedSeats, {
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [buyers, isBuyersLoading, setValue, fieldPrefix, observableBuyerIds]);

  useEffect(() => {
    if (shouldDisplayTTD && !isFirstTimeTDD) {
      setIsFirstTimeTDD(true);
      setIsVisibleFirstTimeAlert(true);
    }
  }, [shouldDisplayTTD, isFirstTimeTDD, setIsFirstTimeTDD]);

  const handleCloseFirstTimeAlert = () => {
    setIsVisibleFirstTimeAlert(false);
  };

  const onMainBuyerChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, value: string) => {
      setValue(`${fieldPrefix}${DealParticipantsField.MAIN_BUYER}`, value, { shouldDirty: true });
    },
    [fieldPrefix, setValue]
  );

  const onMultipleBuyerIdsChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, value: string[]) => {
      const buyerIds = prepareBuyerIdsForBuyerChange(observableMainBuyer, buyersOptionsMap, value);
      multipleBuyersIdsRef.current = buyerIds;
      multipleBuyersValuesRef.current = value;
      setValue(`${fieldPrefix}${DealParticipantsField.BUYER_ID}`, null, { shouldDirty: true });
      setValue(`${fieldPrefix}${DealParticipantsField.BUYER_IDS}`, value, { shouldDirty: true });
    },
    [buyersOptionsMap, fieldPrefix, observableMainBuyer, setValue]
  );

  const onSingleBuyerIdChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, value: string) => {
      const buyerIds = prepareBuyerIdsForBuyerChange(observableMainBuyer, buyersOptionsMap, [value]);
      singleBuyerIdsRef.current = buyerIds;
      setValue(`${fieldPrefix}${DealParticipantsField.BUYER_ID}`, value, { shouldDirty: true });
    },
    [buyersOptionsMap, fieldPrefix, observableMainBuyer, setValue]
  );

  const getBuyerOptionLabel: (o: string) => string = useCallback(
    optionId => {
      const option = buyersOptionsMap[optionId];
      return option?.platform_seat_name || t('N / A');
    },
    [t, buyersOptionsMap]
  );

  return (
    <>
      {isVisibleFirstTimeAlert && (
        <Alert
          sx={{
            marginBottom: 2,
          }}
          data-test="dsp-buyer-info"
          severity="info"
          action={
            <Button onClick={handleCloseFirstTimeAlert}>
              <CloseIcon />
            </Button>
          }
        >
          {t(
            'OpenX automatically pushes TTD deals with seats through The Trade Desk Price Discovery & Provisioning API now.'
          )}
        </Alert>
      )}

      {shouldDisplayTTD && (
        <Alert data-test="dsp-buyer-info" severity="info">
          {t("Choose OpenX buyer name and TTD Buyer ID in the 'Main Buyer' dropdown to enable deal syncing.")}
        </Alert>
      )}

      {isTTDPartnerAndGuaranteedDealType && (
        <Alert severity="info" sx={{ marginTop: 1 }}>
          {t('Set the deal end date to enable deal syncing.')}
        </Alert>
      )}

      {showParticipantBuyersTTD && (
        <ParticipantBuyersTTD
          fieldPrefix={fieldPrefix}
          filteredBuyersOption={filteredBuyersOption}
          getBuyerOptionLabel={getBuyerOptionLabel}
          isBuyersLoading={isBuyersLoading}
          isTTDPartnerAndGuaranteedDealType={isTTDPartnerAndGuaranteedDealType}
          mainBuyer={observableMainBuyer}
          mainBuyerOptions={mainBuyerOptions}
          onMultipleBuyerIdsChange={onMultipleBuyerIdsChange}
          onMainBuyerChange={onMainBuyerChange}
        />
      )}
      {showParticipantBuyersSingle && (
        <ParticipantBuyersSingle
          fieldPrefix={fieldPrefix}
          getBuyerOptionLabel={getBuyerOptionLabel}
          filteredBuyersOption={filteredBuyersOption}
          isBuyersLoading={isBuyersLoading}
          demandPartnerType={demandPartnerType}
          buyerId={observableBuyerId}
          onSingleBuyerIdChange={onSingleBuyerIdChange}
        />
      )}
      {showParticipantBuyersMultiple && (
        <ParticipantBuyersMultiple
          fieldPrefix={fieldPrefix}
          filteredBuyersOption={filteredBuyersOption}
          getBuyerOptionLabel={getBuyerOptionLabel}
          isBuyersLoading={isBuyersLoading}
          onMultipleBuyerIdsChange={onMultipleBuyerIdsChange}
        />
      )}

      {hasDealRevenueCalculatorAccess && (
        <>
          <CommercialAgreementsInfo index={index} />
          <PostAuctionDiscountInfo />
        </>
      )}
    </>
  );
};
